<template>
	<div class="hdl-invite-page p-4">
		<div class="mb-5 flex flex-row justify-center items-center">
			<img src="/static/images/logo.svg" width="46" alt="logo" />
		</div>
		<div class="hdl-heading-secondary text-center mb-5">
			Accept your invite to {{ $workspace.info.name }}
		</div>
		<banner
			hdl-style="red"
			class="my-3 justify-between"
			v-if="!loading && tokenResult !== null && !tokenResult.valid"
		>
			<template v-if="tokenResult.reason == 1">
				<div>This invite token is no longer valid</div>
				<a class="ml-2" href="/account/login/">Go to login</a>
			</template>
			<template v-else-if="tokenResult.reason == 4">
				<div>This invite is for a different account.</div>
				<a class="ml-2" href="/account/logout/">Sign me out</a>
			</template>
			<template v-else>
				<div>There was an unexpected error</div>
				<a class="ml-2" href="/account/login/">Go to login</a>
			</template>
		</banner>
		<div v-if="confirmMethod === 'register'">
			<div class="mb-5">
				<div class="mb-1">Email</div>
				<hdl-textbox
					:model-value="(tokenResult && tokenResult.email)? tokenResult.email : ''"
					hdl-style="auth"
					class="w-full"
					disabled
				/>
			</div>
			<div class="mb-5">
				<div class="mb-1">Name</div>
				<hdl-textbox
					v-model="name"
					placeholder="Enter your full name"
					hdl-style="auth"
					class="w-full"
					@enter="register"
					focus-on-mount
				/>
			</div>
			<div class="mb-5">
				<div class="mb-1">Password</div>
				<hdl-textbox
					v-model="password"
					placeholder="Enter your password"
					hdl-style="auth"
					class="w-full"
					password
					@enter="register"
				/>
			</div>
			<div class="mb-5">
				<div class="mb-1">Password again</div>
				<hdl-textbox
					v-model="passwordAgain"
					placeholder="Enter your password again"
					hdl-style="auth"
					class="w-full"
					password
					@enter="register"
				/>
			</div>
			<div class="mb-8">
				<hdl-button
					@click.prevent="register"
					label="Register"
					hdl-style="primary"
					size="lg"
					class="w-full"
					:disabled="!formIsValid"
				/>
				<!--<google-signin-button />-->
			</div>
			<div class="hdl-subtext text-center">
				By clicking "Register" you agree to the
				<a href="https://medley.so/company/terms-of-service" target="_blank">Terms of Service</a> and
				acknowledge the <a href="https://medley.so/company/privacy-policy" target="_blank">Privacy Policy</a>.
			</div>
		</div>
		<div v-else-if="confirmMethod === 'login'">
			<div class="text-center mb-5">
				Login to your Medley account to accept the invite
			</div>
			<banner
				hdl-style="red"
				class="my-3"
				v-if="!loggingIn && loginError"
			>
				{{ loginError }}
			</banner>
			<div class="mb-5">
				<div class="mb-1">Email</div>
				<hdl-textbox
					:model-value="(tokenResult && tokenResult.email)? tokenResult.email : ''"
					hdl-style="auth"
					class="w-full"
					disabled
				/>
			</div>
			<div class="mb-5">
				<div class="mb-1">Password</div>
				<hdl-textbox
					v-model="loginPassword"
					placeholder="Enter your password"
					hdl-style="auth"
					class="w-full"
					password
					@enter="login"
				/>
			</div>
			<div class="mb-8">
				<hdl-button
					@click.prevent="login"
					label="Login & accept the invite"
					hdl-style="primary"
					size="lg"
					class="w-full"
					:disabled="!loginFormIsValid"
				/>
				<!--<google-signin-button />-->
			</div>
		</div>
	</div>
</template>

<script lang="ts">
import { Vue, Component, Prop } from 'vue-facing-decorator';

import { fetcher, FetchResult } from '@/common/fetcher';
import { InviteTokenResponse } from '@/common/interfaces';

@Component
export default class InvitePage extends Vue {
	password: string = "";
	passwordAgain: string = "";
	name: string = "";

	loading: boolean = false;
	tokenResult: InviteTokenResponse | null = null;

	registering: boolean = false;

	confirmMethod: 'login' | 'register' = 'register';

	loginPassword: string = "";
	loggingIn: boolean = false;
	loginError: string | null = null;

	@Prop({ required: true })
	token: string;

	created(): void {
		this.validateToken();
	}

	validateToken(): void {
		this.loading = true;
		fetcher.Get(
			`/account/invite/${this.token}/validate/`
		).then(
			(result: FetchResult) => {
				if (result.response.ok && result.json.success) {
					this.tokenResult = result.json.data as InviteTokenResponse;

					if (this.tokenResult.userExists) {
						this.confirmMethod = 'login';
					} else {
						this.confirmMethod = 'register';
					}
				}
				this.loading = false;
			}
		)
	}

	register(): void {
		if (!this.formIsValid) {
			return;
		}

		this.registering = true;
		fetcher.Post(
			`/account/invite/${this.token}/register/`,
			{
				password: this.password,
				name: this.name
			}
		).then(
			(result: FetchResult) => {
				if (result.response.ok && result.json.success) {
					window.location.replace('/');
				}
				this.registering = false;
			}
		);
	}

	login(): void {
		if (!this.loginFormIsValid) {
			return;
		}

		this.loggingIn = true;
		this.loginError = null;
		fetcher.Post(
			`/account/invite/${this.token}/login/`,
			{
				password: this.loginPassword
			}
		).then(
			(result: FetchResult) => {
				if (result.response.ok && result.json.success) {
					this.loginError = null;
					window.location.replace('/');
				} else if (result.response.ok && !result.json.success && result.json.code) {
					if (result.json.message) {
						this.loginError = result.json.message;
					} else {
						this.loginError = "There was an un-expected error";
					}
				}
				this.loggingIn = false;
			}
		)
	}

	get loginFormIsValid(): boolean {
		return !!(
			!this.loading
			&& !this.loggingIn
			&& this.confirmMethod === 'login'
			&& this.tokenResult !== null
			&& this.tokenResult.valid
			&& this.tokenResult.email
			&& this.tokenResult.email.length > 0
			&& this.loginPassword.length > 0
		);
	}

	get formIsValid(): boolean {
		return (
			!this.loading
			&& !this.registering
			&& this.tokenResult !== null
			&& this.tokenResult.valid
			&& this.password === this.passwordAgain
			&& this.password.trim().length >= 8
		);
	}
}
</script>

<style lang="scss">

</style>
